import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import useConfigWithExtraValidation from '../../utils/form/useExtraFieldValidation.js';
import { MAX_NAME_LENGTH } from './Constants.js';
import TextField from './TextField.js';

/**
 * Validates a name string against Zillow's name requirements.
 *
 * Validation Rules:
 * - Length: Total (max 250)
 * - Special chars (-') allowed between name parts
 * - Must include both first and last name
 *
 * Valid names:
 * - "John Smith"
 * - "Jean-Pierre Dupont"
 * - "O'Connor Smith"
 * - "José António García"
 *
 * Invalid names:
 * - "-John Smith" (starts with special)
 * - "John--Smith" (consecutive special)
 * - "John* Smith" (invalid character)
 * - "John" (missing last name)
 *
 * @param {string} value - The name to validate.
 * @returns {string|null} Error message if invalid, null if valid.
 */
function getInvalidNameError(value) {
  // Handle non-string inputs
  if (typeof value !== 'string') {
    return 'Please enter a valid name';
  }

  // Check raw input length first to prevent bypassing length limit
  if (value.length > MAX_NAME_LENGTH) {
    return 'Name is too long (maximum 250 characters)';
  }

  // Check if the name starts with spaces
  if (value.startsWith(' ')) {
    return 'Name cannot start with spaces';
  }

  // Trim and normalize spaces for pattern validation
  var trimmedValue = value.trim();
  if (!trimmedValue) {
    return 'Please enter a valid name';
  }

  // Remove multiple consecutive spaces and split
  var nameParts = trimmedValue.replace(/\s+/g, ' ').split(' ');

  // Require at least first and last name
  if (nameParts.length < 2) {
    return 'Please enter both first and last name';
  }

  // Define character sets for name validation
  var BASIC_LATIN = 'A-Za-z'; // Basic Latin alphabet
  var LATIN_SUPPLEMENT = 'À-ÖØ-öø-ÿ'; // Latin-1 supplement (accented characters)
  var SPECIAL_CHARS = "\\s'-"; // Spaces, apostrophes, and hyphens

  // Simplified name validation pattern
  var nameRegex = new RegExp("^[" + BASIC_LATIN + LATIN_SUPPLEMENT + SPECIAL_CHARS + "]+$");

  // Check for invalid patterns before individual name validation
  // Prevent consecutive special characters
  if (/['-]{2,}/.test(trimmedValue)) {
    return 'Name contains consecutive special characters';
  }

  // Prevent special characters at start/end of any name part
  if (nameParts.some(function (part) {
    return /^[-']|[-']$/.test(part);
  })) {
    return 'Names cannot start or end with special characters';
  }

  // Check for invalid characters (allows letters, spaces, hyphens, and apostrophes)
  // Updated to include more Unicode ranges for international names
  if (!nameRegex.test(trimmedValue)) {
    return 'Name contains invalid characters';
  }
  return null;
}

/**
 * Enhanced text field component for name input with specialized validation.
 * Supports international characters, hyphens, and apostrophes.
 * Requires both first and last name.
 *
 * Common Cases:
 * - Standard names (John Smith)
 * - Hyphenated (Mary-Jane Smith)
 * - Apostrophe (O'Brien Smith)
 * - International (José García)
 *
 * Edge Cases:
 * - Single word with special char (O'Brien Smith)
 * - Multiple special chars (Jean-Marie O'Connor)
 * - Accented characters (François-Xavier)
 *
 * @component
 * @param {Object} textFieldProps - Standard TextField props.
 * @returns {JSX.Element} A TextField component with name validation.
 */
function NameField(textFieldProps) {
  return /*#__PURE__*/React.createElement(TextField, _extends({}, textFieldProps, {
    autoComplete: "name",
    config: useConfigWithExtraValidation(textFieldProps.config, getInvalidNameError)
  }));
}
NameField.propTypes = process.env.NODE_ENV !== "production" ? TextField.propTypes : {};
NameField.defaultProps = TextField.defaultProps;

export { NameField as default, getInvalidNameError };
